import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiErrorMessage, launchApi } from '../api';
import { help } from '../helpTexts';
import { cardSx, launchColors, panelSx } from '../launchTheme';
import {
  ProductDetail,
  ProductDuplicate,
  ProductFacet,
  ProductFieldDef,
  ProductHistoryEntry,
  ProductPublishState,
  ProductSection,
} from '../types';
import AssetHoverLink from '../ui/AssetHoverLink';
import { labeledText } from '../ui/CopyButton';
import InfoTip from '../ui/InfoTip';
import ProductImage from '../ui/ProductImage';
import ProgressRing from '../ui/ProgressRing';
import SaveIndicator, { saveStamp, SaveState } from '../ui/SaveIndicator';
import { StatusPill } from '../ui/StatusPill';
import {
  DABAS_ORDER,
  SECTION_LABELS,
  SECTION_ORDER,
  STATUS_OPTIONS,
  dateTimeText,
  eanChecksumOk,
  personName,
  productCommandId,
  shownValue,
  statusLabel,
  statusStyle,
  storedValue,
} from './productLabels';

interface Props {
  productId: string;
  fields: ProductFieldDef[];
  facets: { lists: ProductFacet[]; groups: ProductFacet[]; suppliers: ProductFacet[] };
  canEdit: boolean;
  names: Record<string, string>;
  athenaAssetId?: string;
  onChanged: () => void;
  onBack?: () => void;
  onOpen: (productId: string) => void;
}

type Patch = Record<string, unknown>;

const copy = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
};

const CopyButton: React.FC<{ label: string; text: string; onCopied: (ok: boolean) => void }> = ({ label, text, onCopied }) => (
  <Tooltip title="Kopiera">
    <span>
      <IconButton size="small" aria-label={`Kopiera ${label}`} disabled={!text} onClick={async () => onCopied(await copy(text))} sx={{ p: 0.5 }}>
        <ContentCopyIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </span>
  </Tooltip>
);

const counted = (field: ProductFieldDef) => field.maxLength <= 40 || field.key === 'marketingText';

const FieldEditor: React.FC<{
  field: ProductFieldDef;
  value: string;
  canEdit: boolean;
  onSave: (value: string) => void;
  onCopied: (ok: boolean) => void;
}> = ({ field, value, canEdit, onSave, onCopied }) => {
  const [draft, setDraft] = useState(shownValue(field, value));
  useEffect(() => { setDraft(shownValue(field, value)); }, [field, value]);
  const length = draft.length;
  const tooLong = length > field.maxLength;
  const badEan = field.kind === 'ean' && Boolean(draft) && /^\d+$/.test(draft) && !eanChecksumOk(draft);
  const helper = [
    counted(field) ? `${length}/${field.maxLength}` : '',
    badEan ? 'Fel kontrollsiffra' : '',
    field.dabasCode ? field.dabasCode : '',
  ].filter(Boolean).join(' · ');
  const commit = () => {
    const next = storedValue(field, draft);
    if (next !== value) onSave(next);
  };
  return (
    <TextField
      size="small"
      fullWidth
      label={field.label}
      value={draft}
      multiline={field.multiline}
      minRows={field.multiline ? (field.key === 'marketingText' ? 4 : 2) : undefined}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => { if (canEdit) commit(); }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') setDraft(shownValue(field, value));
        if (event.key === 'Enter' && !field.multiline) (event.target as HTMLInputElement).blur();
      }}
      error={tooLong || badEan}
      helperText={helper || undefined}
      InputProps={{
        readOnly: !canEdit,
        endAdornment: (
          <InputAdornment position="end" sx={{ alignSelf: field.multiline ? 'flex-start' : 'center', mt: field.multiline ? 1 : 0 }}>
            <CopyButton label={field.label} text={draft} onCopied={onCopied} />
          </InputAdornment>
        ),
      }}
      sx={{ bgcolor: launchColors.card }}
    />
  );
};

const ChoiceEditor: React.FC<{
  label: string;
  value: string;
  options: string[];
  canEdit: boolean;
  required?: boolean;
  onSave: (value: string) => void;
}> = ({ label, value, options, canEdit, required, onSave }) => {
  const [draft, setDraft] = useState(value);
  const sent = useRef(value);
  useEffect(() => {
    setDraft(value);
    sent.current = value;
  }, [value]);
  const commit = (next: string) => {
    const trimmed = next.trim();
    if (trimmed === sent.current) return;
    if (trimmed === value || (required && !trimmed)) {
      setDraft(value);
      return;
    }
    sent.current = trimmed;
    onSave(trimmed);
  };
  if (!canEdit) return <TextField size="small" fullWidth label={label} value={value} InputProps={{ readOnly: true }} />;
  return (
    <Autocomplete
      freeSolo
      options={options}
      inputValue={draft}
      onInputChange={(_event, next, reason) => { if (reason !== 'reset') setDraft(next); }}
      onChange={(_event, next) => { if (typeof next === 'string') commit(next); }}
      renderInput={(params) => <TextField {...params} size="small" label={label} onBlur={() => commit(draft)} />}
    />
  );
};

const PublishEditor: React.FC<{
  title: string;
  value: ProductPublishState;
  withImage: boolean;
  canEdit: boolean;
  onSave: (value: Partial<ProductPublishState>) => void;
}> = ({ title, value, withImage, canEdit, onSave }) => {
  const [date, setDate] = useState(value.date || '');
  useEffect(() => { setDate(value.date || ''); }, [value.date]);
  return (
    <Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: launchColors.navy, mb: 1 }}>{title}</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 1.25 }}>
        <TextField select size="small" label={`Finns i ${title.toLocaleLowerCase('sv')}`} value={value.state} disabled={!canEdit} onChange={(event) => onSave({ state: event.target.value as ProductPublishState['state'] })}>
          <MenuItem value="">Inte angivet</MenuItem>
          <MenuItem value="entered">Ja</MenuItem>
          <MenuItem value="not_entered">Nej</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="date"
          label="Kontrollerat"
          value={date}
          disabled={!canEdit}
          InputLabelProps={{ shrink: true }}
          onChange={(event) => setDate(event.target.value)}
          onBlur={() => { if ((date || null) !== value.date) onSave({ date: date || null }); }}
        />
        <TextField select size="small" label="Ska publiceras" value={value.publish} disabled={!canEdit} onChange={(event) => onSave({ publish: event.target.value as ProductPublishState['publish'] })}>
          <MenuItem value="">Inte angivet</MenuItem>
          <MenuItem value="yes">Ja</MenuItem>
          <MenuItem value="no">Nej</MenuItem>
        </TextField>
        {withImage && (
          <TextField
            select
            size="small"
            label="Har bild"
            value={value.withImage === null ? '' : value.withImage ? 'yes' : 'no'}
            disabled={!canEdit}
            onChange={(event) => onSave({ withImage: event.target.value === '' ? null : event.target.value === 'yes' })}
          >
            <MenuItem value="">Inte angivet</MenuItem>
            <MenuItem value="yes">Ja</MenuItem>
            <MenuItem value="no">Nej</MenuItem>
          </TextField>
        )}
      </Box>
    </Box>
  );
};

const Section: React.FC<{ title: string; defaultExpanded?: boolean; onExpand?: () => void; children: React.ReactNode }> = ({ title, defaultExpanded, onExpand, children }) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    TransitionProps={{ unmountOnExit: true }}
    onChange={(_event, expanded) => { if (expanded) onExpand?.(); }}
    sx={{ boxShadow: 'none', border: `1px solid ${launchColors.divider}`, borderRadius: '10px !important', '&::before': { display: 'none' } }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Typography component="h3" sx={{ fontSize: 14, fontWeight: 700, color: launchColors.navy }}>{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const ProductCard: React.FC<Props> = ({ productId, fields, facets, canEdit, names, athenaAssetId, onChanged, onBack, onOpen }) => {
  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [savedAt, setSavedAt] = useState('');
  const [duplicateAsk, setDuplicateAsk] = useState<{ patch: Patch; duplicates: ProductDuplicate[] } | null>(null);
  const [history, setHistory] = useState<ProductHistoryEntry[] | null>(null);
  const [note, setNote] = useState('');
  const [resetKey, setResetKey] = useState(0);

  const load = useCallback(() => {
    setError(null);
    launchApi.productInfoItem(productId)
      .then((next) => { setDetail(next); setHistory(null); })
      .catch((requestError) => setError(apiErrorMessage(requestError, 'Produkten kunde inte hämtas.')));
  }, [productId]);

  useEffect(() => {
    setDetail(null);
    setNotice(null);
    setSaveState('idle');
    load();
  }, [load]);

  const bySection = useMemo(() => {
    const map = new Map<ProductSection, ProductFieldDef[]>();
    for (const field of fields) map.set(field.section, [...(map.get(field.section) ?? []), field]);
    return map;
  }, [fields]);

  const run = async (command: Patch, allowDuplicate = false): Promise<boolean> => {
    if (!detail) return false;
    setSaving(true);
    setSaveState('saving');
    setError(null);
    try {
      const next = await launchApi.productInfoCommand(productId, {
        commandId: productCommandId(),
        expectedVersion: detail.product.version,
        ...command,
        ...(allowDuplicate ? { allowDuplicate: true } : {}),
      });
      setDetail(next);
      setHistory(null);
      setSaveState('saved');
      setSavedAt(saveStamp());
      onChanged();
      return true;
    } catch (requestError: any) {
      setSaveState('error');
      setResetKey((value) => value + 1);
      const data = requestError?.response?.data;
      if (requestError?.response?.status === 409 && Array.isArray(data?.duplicates)) {
        setDuplicateAsk({ patch: command, duplicates: data.duplicates });
      } else if (requestError?.response?.status === 409) {
        setNotice('Någon annan hann ändra produkten. Kortet har laddats om.');
        load();
      } else {
        setError(apiErrorMessage(requestError, 'Ändringen kunde inte sparas.'));
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = (patch: Patch) => { void run({ type: 'update', patch }); };
  const copied = (ok: boolean) => setToast(ok ? 'Kopierat.' : 'Det gick inte att kopiera.');

  if (!detail) {
    return (
      <Box sx={[cardSx, { p: 2 }]}>
        {error ? <Alert severity="error">{error}</Alert> : <LinearProgress />}
      </Box>
    );
  }

  const { product, readiness, warnings, yearbook } = detail;
  const pill = statusStyle(product.status);
  const dabasText = DABAS_ORDER.map((key) => {
    const field = fields.find((item) => item.key === key);
    const value = product.fields[key] || '';
    return field && value ? `${field.label}${field.dabasCode ? ` (${field.dabasCode})` : ''}: ${value}` : '';
  }).filter(Boolean).join('\n');
  const allText = labeledText([
    ['Artikelnummer', product.articleNumber],
    ['Ersatt av', product.replacedBy],
    ['Status', product.status ? statusLabel(product.status) : ''],
    ['Leverantör', product.supplier],
    ['Varugrupp', product.group],
    ['Lista', product.list],
    ...SECTION_ORDER.flatMap((section) => (bySection.get(section) ?? []).map((field): [string, string] => [field.label, shownValue(field, product.fields[field.key] || '')])),
  ]);
  const extra = Object.entries(product.extra);
  const meta = [product.supplier, product.group, product.list].filter(Boolean).join(' · ');

  return (
    <Box component="section" aria-label={`Produkt ${product.articleNumber}`} sx={[cardSx, { p: { xs: 1, sm: 1.5 }, bgcolor: launchColors.section }]}>
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={panelSx}>
          {onBack && (
            <IconButton aria-label="Tillbaka till listan" onClick={onBack} size="small"><ArrowBackIcon /></IconButton>
          )}
          <ProductImage assetId={athenaAssetId} title={detail.name || product.articleNumber} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: launchColors.textSecondary }}>
              {athenaAssetId ? (
                <>
                  <AssetHoverLink assetId={athenaAssetId} title={detail.name || product.articleNumber} hasThumbnail>{product.articleNumber}</AssetHoverLink>
                  {meta ? ` · ${meta}` : ''}
                </>
              ) : [product.articleNumber, meta].filter(Boolean).join(' · ')}
            </Typography>
            <Typography component="h2" sx={{ fontSize: 20, fontWeight: 700, color: launchColors.navy, lineHeight: 1.25 }}>
              {detail.name || 'Namn saknas'}
            </Typography>
            <SaveIndicator state={saveState} savedAt={savedAt} />
          </Box>
          <Stack spacing={0.75} alignItems="flex-end">
            <StatusPill label={statusLabel(product.status)} fg={pill.fg} bg={pill.bg} />
            <Stack alignItems="center" spacing={0.25} title={readiness.missing.length ? `Saknas: ${readiness.missing.join(', ')}` : 'Klar för Dabas'}>
              <ProgressRing value={readiness.total ? (readiness.done / readiness.total) * 100 : 0} size={48} color={readiness.missing.length === 0 ? launchColors.moss : readiness.done * 2 >= readiness.total ? launchColors.warningMarker : launchColors.dangerText} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: launchColors.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{readiness.done}/{readiness.total}</Typography>
            </Stack>
          </Stack>
        </Stack>
        {saving && <LinearProgress />}
        {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
        {notice && <Alert severity="info" onClose={() => setNotice(null)}>{notice}</Alert>}
        {product.replacedBy && (
          <Alert severity="info">Artikelnumret har bytts till {product.replacedBy}.</Alert>
        )}
        {warnings.map((warning) => (
          <Alert key={`${warning.kind}-${'field' in warning ? warning.field : ''}`} severity="warning">
            {warning.message}
            {warning.kind === 'duplicate' && (
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                {warning.others.map((other) => (
                  <Chip key={other.id} size="small" label={`${other.list}: ${other.name || other.articleNumber}`} onClick={() => onOpen(other.id)} />
                ))}
              </Stack>
            )}
          </Alert>
        ))}
        <Stack spacing={1.5} sx={panelSx}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: readiness.missing.length ? launchColors.warningText : launchColors.mossDark }}>
              Klar för Dabas {readiness.done}/{readiness.total}
            </Typography>
            {readiness.missing.length > 0 && (
              <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Saknas: {readiness.missing.join(', ')}</Typography>
            )}
            <InfoTip text={help.productInfoReady} />
            <Box sx={{ flex: 1 }} />
            <Button size="small" startIcon={<ContentCopyIcon />} disabled={!dabasText} onClick={async () => copied(await copy(dabasText))}>
              Kopiera alla Dabas-fält
            </Button>
            <Button size="small" startIcon={<ContentCopyIcon />} disabled={!allText} onClick={async () => copied(await copy(allText))}>
              Kopiera alla fält
            </Button>
          </Stack>

          <Box key={resetKey} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1.25 }}>
            <ChoiceEditor label="Artikelnummer" value={product.articleNumber} options={[]} canEdit={canEdit} required onSave={(value) => save({ articleNumber: value })} />
            <ChoiceEditor label="Ersatt av" value={product.replacedBy} options={[]} canEdit={canEdit} onSave={(value) => save({ replacedBy: value })} />
            <TextField select size="small" label="Status" value={product.status} disabled={!canEdit} onChange={(event) => save({ status: event.target.value })}>
              <MenuItem value="">Ingen status</MenuItem>
              {STATUS_OPTIONS.map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
            </TextField>
            <ChoiceEditor label="Leverantör" value={product.supplier} options={facets.suppliers.map((item) => item.name)} canEdit={canEdit} onSave={(value) => save({ supplier: value })} />
            <ChoiceEditor label="Varugrupp" value={product.group} options={facets.groups.map((item) => item.name)} canEdit={canEdit} onSave={(value) => save({ group: value })} />
            <ChoiceEditor label="Lista" value={product.list} options={facets.lists.map((item) => item.name)} canEdit={canEdit} required onSave={(value) => save({ list: value })} />
          </Box>
        </Stack>

        {SECTION_ORDER.map((section) => (
          <Section key={section} title={SECTION_LABELS[section]} defaultExpanded={section === 'names'}>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.5 }}>
              {(bySection.get(section) ?? []).map((field) => (
                <Box key={`${field.key}-${resetKey}`} sx={{ gridColumn: field.multiline ? '1 / -1' : undefined }}>
                  <FieldEditor
                    field={field}
                    value={product.fields[field.key] || ''}
                    canEdit={canEdit}
                    onSave={(value) => save({ fields: { [field.key]: value } })}
                    onCopied={copied}
                  />
                </Box>
              ))}
            </Box>
          </Section>
        ))}

        <Section title="Dabas och hemsida">
          <Stack spacing={2}>
            <PublishEditor title="Dabas" value={product.dabas} withImage canEdit={canEdit} onSave={(value) => save({ dabas: value })} />
            <PublishEditor title="Hemsidan" value={product.website} withImage={false} canEdit={canEdit} onSave={(value) => save({ website: value })} />
          </Stack>
        </Section>

        <Section title={`Anteckningar (${product.notes.length})`} defaultExpanded={product.notes.length > 0}>
          <Stack spacing={1}>
            {product.notes.length === 0 && <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Inga anteckningar.</Typography>}
            {[...product.notes].reverse().map((item, index) => (
              <Box key={`${item.at}-${index}`} sx={{ borderLeft: `3px solid ${launchColors.sand}`, pl: 1.25 }}>
                <Typography sx={{ fontSize: 14, whiteSpace: 'pre-wrap' }}>{item.text}</Typography>
                <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>
                  {[personName(item.by, names), item.at.length > 10 ? dateTimeText(item.at) : item.at].filter(Boolean).join(' · ')}
                </Typography>
              </Box>
            ))}
            {canEdit && (
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField size="small" fullWidth multiline label="Ny anteckning" value={note} onChange={(event) => setNote(event.target.value)} inputProps={{ maxLength: 1000 }} />
                <Button
                  variant="outlined"
                  disabled={!note.trim() || saving}
                  onClick={async () => {
                    if (await run({ type: 'addNote', text: note })) setNote('');
                  }}
                >
                  Lägg till
                </Button>
              </Stack>
            )}
          </Stack>
        </Section>

        <Section title={`I årskalendern (${yearbook.length})`}>
          {yearbook.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Artikelnumret finns inte i årskalendern.</Typography>
          ) : (
            <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
              {yearbook.map((item) => (
                <Chip key={`${item.sheetId}-${item.rowId}`} size="small" label={`${item.chain}${item.statedWeek ? ` v${item.statedWeek}` : ''}${item.announcementDate ? ` (${item.announcementDate})` : ''}`} />
              ))}
            </Stack>
          )}
        </Section>

        {extra.length > 0 && (
          <Section title="Övrigt från Excel">
            <Stack spacing={0.5}>
              {extra.map(([key, value]) => (
                <Typography key={key} sx={{ fontSize: 13 }}><Box component="span" sx={{ fontWeight: 700 }}>{key}:</Box> {value}</Typography>
              ))}
              {product.source && (
                <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>Från bladet {product.source.sheet}, rad {product.source.row}.</Typography>
              )}
            </Stack>
          </Section>
        )}

        <Section
          title="Historik"
          onExpand={() => {
            if (history) return;
            launchApi.productInfoHistory(productId).then(setHistory).catch(() => setHistory([]));
          }}
        >
          {!history ? <LinearProgress /> : history.length === 0 ? (
            <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }}>Ingen historik.</Typography>
          ) : (
            <Stack spacing={1.25}>
              {history.map((entry) => (
                <Box key={entry.id}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{entry.summary}</Typography>
                  <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>{[personName(entry.by, names), dateTimeText(entry.at)].filter(Boolean).join(' · ')}</Typography>
                  {entry.changes.map((change) => (
                    <Typography key={change.field} sx={{ fontSize: 12, color: launchColors.text, wordBreak: 'break-word' }}>
                      {change.label}: {change.before || 'tomt'} → {change.after || 'tomt'}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Stack>
          )}
        </Section>
      </Stack>

      <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} message={toast || ''} />
      <Dialog open={Boolean(duplicateAsk)} onClose={() => setDuplicateAsk(null)}>
        <DialogTitle>Artikelnumret finns redan</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>Samma artikelnummer finns på:</Typography>
          {duplicateAsk?.duplicates.map((item) => (
            <Typography key={item.id} sx={{ fontSize: 14 }}>{item.list}: {item.articleNumber} {item.name}</Typography>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDuplicateAsk(null)}>Avbryt</Button>
          <Button
            variant="contained"
            onClick={() => {
              const ask = duplicateAsk;
              setDuplicateAsk(null);
              if (ask) void run(ask.patch, true);
            }}
          >
            Spara ändå
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductCard;
